import {
	getGetSpaceQueryOptions,
	getListSpacesQueryOptions,
	listSpaces,
} from '@cocoonspace/sdk-js/domains/spaces/spaces'
import type { Zone } from '@cocoonspace/sdk-js/types/zone'
import { defaultI18nNs } from '@cocoonspace/shared/config/default-i-18-n-ns-config'
import { searchConfig } from '@cocoonspace/shared/config/search-config'
import { QueryClient, dehydrate } from '@tanstack/react-query'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { SpaceDetailsPage } from '~/domains/spaces/components/space-details/space-details-page'
import { getCapacityRange } from '~/domains/spaces/utils/get-capacity-range'

export default SpaceDetailsPage

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
	const queryClient = new QueryClient()
	const zone = (params?.zone as Zone) || 'paris'

	try {
		const spaceData = await queryClient.fetchQuery(
			getGetSpaceQueryOptions(params?.slug as string),
		)

		if (!spaceData.data.offers?.includes('desk')) return { notFound: true }

		const district =
			searchConfig.districts[zone]?.find(
				(district) => district.zip === spaceData.data.address.postal_code,
			) || null

		await queryClient.fetchQuery(
			getListSpacesQueryOptions(
				{
					zone: spaceData.data.zone as Zone,
					capacity: getCapacityRange(spaceData.data.capacity, 2),
					limit: 6,
					...(district && {
						latitude: district.latitude,
						longitude: district.longitude,
					}),
				},
				{
					query: {
						select: (spaces) =>
							spaces.data.filter(({ id }) => id !== spaceData.data.id),
					},
				},
			),
		)

		return {
			props: {
				...(await serverSideTranslations(locale as string, [
					...defaultI18nNs,
					'space',
					'features',
					'extras',
					'booking',
					'promotions',
					'myAccountPaymentMethods',
					'myAccountPersonalInfos',
					'billingAddressForm',
					'infosPersoForm',
				])),
				district,
				dehydratedState: dehydrate(queryClient),
			},
			revalidate: 24 * 60 * 60, // 24h
		}
	} catch (e) {
		return { notFound: true }
	}
}

export const getStaticPaths: GetStaticPaths = async () => {
	const [spaceDataParis, spaceDataLyon] = await Promise.all([
		listSpaces({ zone: 'paris', select: ['slug'] }).then((res) => res.data),
		listSpaces({ zone: 'lyon', select: ['slug'] }).then((res) => res.data),
	])

	return {
		paths: [
			...spaceDataParis.map((space) => ({
				params: { zone: 'paris', slug: space.slug },
			})),
			...spaceDataLyon.map((space) => ({
				params: { zone: 'lyon', slug: space.slug },
			})),
		],
		fallback: 'blocking',
	}
}
